import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCountryByIdQuery, useShippingAddressCreateMutation, useShippingAddressUpdateMutation } from 'api';
import { Form, FormProps, notification } from 'components';
import { IAddressAddEditModal } from 'feature/clientTabs/shipping/shippingAddresses/addEdit';
import { ErrorMappingValueType } from 'typings';
import { ShippingAddressFormType } from 'typings/form/client/shipping';
import { getUpdatedPayload } from 'utils/helpers';

export const useAddressAddEditModal = (props: IAddressAddEditModal) => {
  const { onModalStateChange, dataToEdit, modalState, clientId } = props;
  const [states, setStates] = useState([]);
  const [form] = Form.useForm();
  const { id } = useParams();
  const [notificationApi, contextHolder] = notification.useNotification();

  const { data: countryData } = useCountryByIdQuery(
    { countryId: dataToEdit?.countryId },
    {
      skip: !modalState || !dataToEdit,
    }
  );
  const [createShippingAddress, { isLoading: createIsLoading }] = useShippingAddressCreateMutation();
  const [updateShippingAddress, { isLoading: updateIsLoading }] = useShippingAddressUpdateMutation();

  const handleCountryChange = useCallback(
    (_: string, value: Record<string, any>) => {
      setStates(value.states || []);
      form.setFieldsValue({ state: undefined });
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onFinish: FormProps<ShippingAddressFormType>['onFinish'] = useCallback(
    (values: ShippingAddressFormType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.clientId = id as string;
      if (clientId) {
        payload.clientId = clientId;
      }
      if (dataToEdit) {
        payload.id = dataToEdit.id;
      }
      (dataToEdit ? updateShippingAddress(payload) : createShippingAddress(payload))
        .then(({ data, error }: any) => {
          if (data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: `Shipping address was successfully ${dataToEdit ? 'updated' : 'created'}.`,
            });
            onCancel();
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [createShippingAddress, updateShippingAddress, openNotificationWithIcon, id, onCancel, dataToEdit, clientId]
  );

  useEffect(() => {
    if (dataToEdit && modalState) {
      form.setFieldsValue(dataToEdit);
    }
    if (countryData?.states.length) {
      setStates(countryData.states);
    }
  }, [dataToEdit, form, modalState, countryData]);

  return {
    onFinish,
    handleCountryChange,
    onCancel,
    contextHolder,
    form,
    states,
    isLoading: createIsLoading || updateIsLoading,
  };
};
